import React, { useEffect, useRef } from 'react';

interface TypedLineProps extends React.HTMLAttributes<HTMLElement> {
  /** The element it is drawn as: the page title, a bullet, the button. */
  tag: 'h1' | 'span' | 'a';
  /** The words as the screen holds them. */
  value: string;
  /** The words, every time they change under the person's hands. */
  onWords: (words: string) => void;
  /** Longer than this is refused while typing and cut on a paste. */
  maxLength?: number;
  /** Not to be typed in right now (an AI answer is arriving). */
  locked?: boolean;
  placeholder?: string;
  href?: string;
}

const caretToEnd = (el: HTMLElement) => {
  const selection = window.getSelection();
  const range = document.createRange();
  range.selectNodeContents(el);
  range.collapse(false);
  selection?.removeAllRanges();
  selection?.addRange(range);
};

/**
 * One line of the job page that is typed straight on the page: the page
 * title, a bullet, the button text.
 *
 * It hands its words over WHILE they are typed (Steven, 6 Oct 2026). These
 * three used to do so only when the caret left them, so nothing was stored
 * and Undo knew nothing until then, and the Undo arrow had to take the caret
 * out to make them speak.
 *
 * Built as the section heading next to it (VacancyContentInput): the page,
 * not React, owns the text while the person is in it, because drawing it
 * again on every keystroke throws the caret to the start. A value that comes
 * from OUTSIDE (Undo, Redo, a reload, the AI helper) is put on screen; the
 * line's own words coming back are left alone.
 */
const TypedLine: React.FC<TypedLineProps> = ({
  tag,
  value,
  onWords,
  maxLength,
  locked = false,
  ...rest
}) => {
  const ref = useRef<HTMLElement | null>(null);
  const lastReported = useRef<string | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const incoming = value ?? '';
    if (el.textContent === incoming) return;
    if (lastReported.current === incoming) return;

    el.textContent = incoming;
    lastReported.current = incoming;
    if (document.activeElement === el) caretToEnd(el);
  }, [value]);

  const report = (el: HTMLElement) => {
    let words = el.textContent ?? '';
    if (maxLength !== undefined && words.length > maxLength) {
      words = words.slice(0, maxLength);
      el.textContent = words;
      caretToEnd(el);
    }
    if (words === lastReported.current && words === (value ?? '')) return;
    lastReported.current = words;
    onWords(words);
  };

  return React.createElement(tag, {
    ...rest,
    ref,
    // The screen's own Undo and Redo cover it, and leave the caret in it.
    'data-page-undo': '',
    contentEditable: !locked,
    suppressContentEditableWarning: true,
    // Refuse a character over the limit BEFORE it lands; typing over a
    // selection replaces it, so the selected characters do not count.
    onBeforeInput: (e: React.FormEvent<HTMLElement>) => {
      if (maxLength === undefined) return;
      const el = e.currentTarget;
      const isInsert = !(e.nativeEvent as InputEvent).inputType?.startsWith(
        'delete'
      );
      const selection = window.getSelection();
      const selected =
        selection &&
        selection.rangeCount > 0 &&
        el.contains(selection.anchorNode) &&
        el.contains(selection.focusNode)
          ? selection.toString().length
          : 0;
      if (isInsert && (el.textContent ?? '').length - selected >= maxLength)
        e.preventDefault();
    },
    onInput: (e: React.FormEvent<HTMLElement>) => report(e.currentTarget),
    onBlur: (e: React.FocusEvent<HTMLElement>) => report(e.currentTarget),
    // Plain words only, put in where the caret is.
    onPaste: (e: React.ClipboardEvent<HTMLElement>) => {
      e.preventDefault();
      const el = e.currentTarget;
      const pasted = e.clipboardData.getData('text/plain').replace(/\s+/g, ' ');
      const selection = window.getSelection();
      const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
      const inside = !!range && el.contains(range.commonAncestorContainer);
      const selected = inside && range ? range.toString().length : 0;
      const room =
        maxLength === undefined
          ? pasted.length
          : maxLength - ((el.textContent ?? '').length - selected);
      const text = pasted.slice(0, Math.max(0, room));

      if (inside && range) {
        range.deleteContents();
        const node = document.createTextNode(text);
        range.insertNode(node);
        range.setStartAfter(node);
        range.collapse(true);
        selection?.removeAllRanges();
        selection?.addRange(range);
      } else {
        el.textContent = (el.textContent ?? '') + text;
      }
      report(el);
    },
  });
};

export default TypedLine;
